(() => {
  if (customElements.get('pu-product-explorer')) return;
  const cm = value => `${Math.round(value * 1000) / 10} cm`;
  const point = ([x, y], width = 1200, height = 960) => `${x * width},${y * height}`;
  const segment = (a, b) => `M${point(a)}L${point(b)}`;
  const svgNode = (name, attrs) => {
    const node = document.createElementNS('http://www.w3.org/2000/svg', name);
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
    return node;
  };
  function labelPositions(axes, width) {
    if (!width) return axes.map(axis => axis.label);
    const height = width * .8, boxWidth = 88, boxHeight = 44;
    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
    const candidates = axes.map(axis => {
      const origin = [axis.label[0] * width, axis.label[1] * height];
      const offsets = [[0, 0]];
      for (const dy of [-2, -1, 1, 2]) offsets.push([0, dy * 26]);
      for (const dx of [-1, 1]) for (const dy of [-1, 0, 1]) offsets.push([dx * 96, dy * 52]);
      for (const x of [52, width / 2, width - 52]) for (const y of [30, height / 2, height - 30]) offsets.push([x - origin[0], y - origin[1]]);
      return offsets.map(([dx, dy]) => {
        const p = [clamp(origin[0] + dx, 52, width - 52), clamp(origin[1] + dy, 30, height - 30)];
        return { p, cost: (p[0] - origin[0]) ** 2 + (p[1] - origin[1]) ** 2 };
      }).sort((a, b) => a.cost - b.cost);
    });
    let bestCost = Infinity, best = axes.map(axis => [axis.label[0] * width, axis.label[1] * height]);
    function place(index, positions, cost) {
      if (cost >= bestCost) return;
      if (index === axes.length) { bestCost = cost; best = positions; return; }
      for (const candidate of candidates[index]) {
        if (cost + candidate.cost >= bestCost) break;
        if (positions.some(p => Math.abs(p[0] - candidate.p[0]) < boxWidth + 6 && Math.abs(p[1] - candidate.p[1]) < boxHeight + 6)) continue;
        place(index + 1, [...positions, candidate.p], cost + candidate.cost);
      }
    }
    place(0, [], 0);
    return best.map(([x, y]) => [x / width, y / height]);
  }
  class ProductExplorer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      const source = this.querySelector('[data-explorer-data]');
      if (!source) return;
      this.data = JSON.parse(source.textContent);
      const heroImage = this.querySelector('[data-hero]');
      // The artwork may still be waiting for project-unity-deferred-images (data-pu-src).
      this.heroSource = { src: heroImage.dataset.puSrc || heroImage.src, srcset: heroImage.dataset.puSrcset || heroImage.getAttribute('srcset') || '' };
      this.dialog = this.querySelector('[data-dialog]');
      const spaceButton = this.querySelector('[data-space-view]');
      const galleryButton = this.closest('.shopify-section')?.querySelector('.pu-product-ar-button');
      const spaceViewer = document.getElementById(spaceButton?.dataset.spaceTarget || '');
      if (spaceButton && (galleryButton || spaceViewer)) {
        spaceButton.hidden = false;
        if (spaceViewer || galleryButton?.hasAttribute('aria-haspopup')) spaceButton.setAttribute('aria-haspopup', 'dialog');
        spaceButton.addEventListener('click', () => {
          const viewer = document.getElementById(galleryButton?.dataset.puSpaceViewerTarget || spaceButton.dataset.spaceTarget || '');
          if (viewer && typeof viewer.open === 'function') {
            viewer.open(galleryButton?.dataset.puSpaceViewerProduct || spaceButton.dataset.spaceProduct, spaceButton);
          } else {
            galleryButton?.click();
          }
        });
      }
      this.selectedPartId = this.data.initialPart; this.view = 'studio'; this.featureIndex = 0;
      const points = this.querySelector('[data-points]');
      this.data.hotspotIds.forEach(id => {
        const feature = this.data.features.find(f => f.id === id);
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'pu-explorer__point'; button.dataset.feature = id;
        button.setAttribute('aria-haspopup', 'dialog'); button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-label', `Explore ${feature.title}`);
        const dot = document.createElement('span'); dot.setAttribute('aria-hidden', 'true'); button.append(dot); points.append(button);
      });
      const poses = this.querySelector('[data-poses]');
      if (this.data.images.alternate) {
        poses.hidden = false;
        ['primary', 'alternate'].forEach(pose => {
          const button = document.createElement('button'); button.type = 'button'; button.className = 'pu-explorer__chip';
          button.dataset.pose = pose; button.textContent = this.data[`${pose}Pose`];
          button.setAttribute('aria-pressed', pose === 'primary'); poses.append(button);
        });
      }
      this.data.dimensions.parts.forEach(part => {
        const option = document.createElement('option'); option.value = part.id; option.textContent = part.name;
        this.querySelector('[data-part]').append(option);
      });
      this.querySelectorAll('[data-feature]').forEach(button => button.addEventListener('click', () => this.openFeature(button.dataset.feature, button)));
      this.querySelectorAll('[data-pose]').forEach(button => button.addEventListener('click', () => this.setPose(button.dataset.pose)));
      this.querySelector('[data-dimensions]')?.addEventListener('click', event => this.openDimensions(event.currentTarget));
      document.querySelectorAll(`[data-explorer-dimensions="${this.id}"]`).forEach(link => link.addEventListener('click', event => {
        event.preventDefault(); this.openDimensions(link);
      }));
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        this.querySelectorAll('[data-feature]').forEach(b => b.setAttribute('aria-expanded', 'false'));
        if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      });
      this.querySelectorAll('[data-step]').forEach(button => button.addEventListener('click', () => {
        const i = (this.featureIndex + Number(button.dataset.step) + this.data.features.length) % this.data.features.length;
        this.openFeature(this.data.features[i].id);
      }));
      this.querySelector('[data-part]').addEventListener('change', event => { this.selectedPartId = event.target.value; this.renderPlan(); });
      this.querySelectorAll('[data-view]').forEach(button => button.addEventListener('click', () => { this.view = button.dataset.view; this.renderPlan(); }));
      this.querySelector('[data-access]').addEventListener('change', () => this.renderPlan());
      this.resizeObserver = new ResizeObserver(() => { this.layoutPoints(); if (this.dialog.open && !this.querySelector('[data-plan]').hidden) this.renderPlan(); });
      this.resizeObserver.observe(this.querySelector('.pu-explorer__scene'));
      this.resizeObserver.observe(this.querySelector('[data-plan-canvas]'));
      this.layoutPoints();
      this.dataset.ready = 'true';
    }
    disconnectedCallback() { this.resizeObserver?.disconnect(); }
    layoutPoints() {
      this.querySelectorAll('.pu-explorer__point').forEach(button => {
        const feature = this.data.features.find(f => f.id === button.dataset.feature);
        const position = [feature.x, feature.y];
        button.style.left = `${position[0] * 100}%`; button.style.top = `${position[1] * 100}%`;
      });
    }

    setPose(pose) {
      const alternate = pose === 'alternate' && Boolean(this.data.images.alternate), img = this.querySelector('[data-hero]');
      // Keep responsive primary artwork when a detail opens. An alternate pose
      // must remove the primary srcset so the browser actually changes pictures.
      img.removeAttribute('data-pu-srcset'); img.removeAttribute('data-pu-src');
      img.srcset = alternate ? '' : this.heroSource.srcset;
      img.src = alternate ? this.data.images.alternate : this.heroSource.src;
      img.alt = `${this.data.name}, ${this.data[alternate ? 'alternatePose' : 'primaryPose'].toLowerCase()}, in an illustrative room`;
      this.querySelector('[data-points]').hidden = alternate;
      this.querySelectorAll('[data-pose]').forEach(b => b.setAttribute('aria-pressed', b.dataset.pose === pose));
      const note = this.querySelector('[data-pose-note]');
      note.textContent = alternate ? `Select ${this.data.primaryPose.toLowerCase()} to explore the details.` : '';
      note.hidden = !alternate;
    }
    openFeature(id, opener) {
      const index = this.data.features.findIndex(f => f.id === id); if (index < 0) return;
      this.featureIndex = index; const f = this.data.features[index];
      this.dialog.dataset.mode = 'feature';
      if (opener) this.opener = opener;
      this.setPose('primary');
      this.querySelector('[data-plan]').hidden = true; this.querySelector('[data-detail]').hidden = false;
      this.querySelector('[data-detail-title]').textContent = f.title;
      this.querySelector('[data-detail-body]').textContent = f.body;
      const img = this.querySelector('[data-detail-image]'); img.src = this.data.images[f.image]; img.alt = `${f.title} on the ${this.data.name}`;
      const specs = this.querySelector('[data-detail-specs]'); specs.replaceChildren(); specs.hidden = !f.specs?.length;
      (f.specs || []).forEach(spec => {
        const row = document.createElement('div'), label = document.createElement('dt'), value = document.createElement('dd');
        label.textContent = spec.label; value.textContent = spec.value;
        if (spec.note) { const note = document.createElement('span'); note.textContent = spec.note; value.append(note); }
        row.append(label, value); specs.append(row);
      });
      this.querySelectorAll('[data-feature]').forEach(b => b.setAttribute('aria-expanded', b.dataset.feature === id));
      this.dialog.setAttribute('aria-labelledby', this.querySelector('[data-detail-title]').id);
      if (this.dialog.open) { this.dialog.scrollTop = 0; return; }
      // Opening a modal dialog re-styles the whole page behind it (about 70 ms on a
      // throttled phone), so let the tap paint first and open on the next task (INP).
      // A second tap in that gap only swaps the detail shown.
      if (this.opening) return;
      this.opening = true;
      window.requestAnimationFrame(() => window.setTimeout(() => {
        this.opening = false;
        if (!this.dialog.open) this.dialog.showModal();
        this.dialog.scrollTop = 0;
      }, 0));
    }
    openDimensions(opener) {
      this.opener = opener; this.selectedPartId = this.data.initialPart; this.view = 'studio';
      this.dialog.dataset.mode = 'dimensions';
      this.querySelector('[data-part]').value = this.selectedPartId;
      this.querySelector('[data-detail]').hidden = true; this.querySelector('[data-plan]').hidden = false;
      this.dialog.setAttribute('aria-labelledby', this.querySelector('[data-plan] h2').id);
      if (!this.dialog.open) this.dialog.showModal();
      this.renderPlan(); this.dialog.scrollTop = 0;
    }
    renderPlan() {
      const d = this.data.dimensions, scene = this.view === 'top' ? d.topView : d;
      const part = scene.parts.find(p => p.id === this.selectedPartId);
      const img = this.querySelector('[data-plan-image]'); img.src = this.data.images[this.view === 'top' ? 'top' : 'dimensions']; img.alt = `${this.view === 'top' ? 'Overhead' : 'Cutaway studio'} view of the ${this.data.name} setup`;
      this.querySelectorAll('[data-view]').forEach(b => b.setAttribute('aria-pressed', b.dataset.view === this.view));
      const svg = this.querySelector('[data-plan-svg]'); svg.replaceChildren();
      if (this.querySelector('[data-access]').checked) svg.append(svgNode('polygon', { points: scene.access.polygon.map(p => point(p)).join(' '), class: 'pu-explorer__access-polygon' }));
      svg.append(svgNode('polygon', { points: part.footprint.map(p => point(p)).join(' '), class: 'pu-explorer__footprint' }));
      const labels = this.querySelector('[data-dimension-labels]'); labels.replaceChildren();
      const positions = labelPositions(part.axes, this.querySelector('[data-plan-canvas]').clientWidth);
      part.axes.forEach((axis, i) => {
        svg.append(svgNode('path', { d: segment(axis.witness_start, axis.start) + segment(axis.witness_end, axis.end), class: 'pu-explorer__witness' }), svgNode('path', { d: segment(axis.start, axis.end), class: 'pu-explorer__axis' }), svgNode('path', { d: segment([(axis.start[0] + axis.end[0]) / 2, (axis.start[1] + axis.end[1]) / 2], positions[i]), class: 'pu-explorer__witness' }));
        const tag = document.createElement('div'); tag.className = 'pu-explorer__dimension-tag'; tag.style.left = `${positions[i][0] * 100}%`; tag.style.top = `${positions[i][1] * 100}%`; tag.setAttribute('aria-hidden', 'true');
        const label = document.createElement('span'); label.textContent = part.dimension_labels[axis.axis]; tag.append(label, document.createTextNode(cm(part.dimensions_m[axis.axis]))); labels.append(tag);
      });
      this.querySelector('[data-part-title]').textContent = part.name;
      this.querySelector('[data-measure-note]').textContent = part.measurement_note;
      const values = this.querySelector('[data-measurements]'); values.replaceChildren();
      part.dimensions_m.forEach((value, i) => { const row = document.createElement('div'), dt = document.createElement('dt'), dd = document.createElement('dd'); dt.textContent = part.dimension_labels[i]; dd.textContent = cm(value); row.append(dt, dd); values.append(row); });
      const all = this.querySelector('[data-all-measurements]'); all.replaceChildren();
      d.parts.forEach(p => { const row = document.createElement('p'); row.textContent = `${p.name}: ${p.dimensions_m.map(cm).join(' × ')} (${p.dimension_labels.join(' × ').toLowerCase()}).`; all.append(row); });
    }
  }
  customElements.define('pu-product-explorer', ProductExplorer);
})();
